// SPDX-License-Identifier: UNLICENSED (Adjust as needed for your project)
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import RoleClaim from './components/RoleClaim';
import BlubStaking from './components/BlubStaking';
import TokenSwap from './components/TokenSwap';
import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { 
  ConnectWallet, 
  Web3Button,
  useContract,
  useAddress,
  useTokenBalance,
  darkTheme,
  useNFT,
  useContractMetadata
} from "@thirdweb-dev/react";
import NFTCard from "./components/NFTCard";
import UnstakedNFTCard from "./components/UnstakedNFTCard";
import LandingPage from "./components/LandingPage"; // Import the new component
import "./styles/Home.css";
import { BigNumber, ethers } from "ethers";
// React Icons can be removed if custom SVG components fully replace them
// import { FaDiscord } from 'react-icons/fa';
// import { FaExternalLinkAlt } from 'react-icons/fa';
// import { FaExchangeAlt } from 'react-icons/fa';
// import { FaUser } from 'react-icons/fa';
// import { FaCoins } from 'react-icons/fa';
// import { FaShieldAlt } from 'react-icons/fa';

// IconWrapper - keep if custom icons are complex, or embed SVG directly
const IconWrapper = ({ icon }: { icon: React.ReactNode }) => {
  return <>{icon}</>;
};

interface NftBalance { // Used by ownedTokens state
  tokenId: string;
}

// PrizeEvent - OLD, can likely be removed
// interface PrizeEvent {
//   prizeType: number;
//   tokenAddress: string;
//   amount: string;
//   quantity: string;
// }

// PrizeOutcome - OLD, from PRIZE_OUTCOMES constant, can be removed
// interface PrizeOutcome {
//   name: string;
//   probability: number;
//   prizeType: number;
//   tokenAddress: string;
//   amount: string | number;
//   tokenId: number;
//   quantity: number;
//   percentage: number;
// }

// PrizeGivenEvent - OLD, can be removed
// type PrizeGivenEvent = {
//   player: string;
//   pType: number;
//   token: string;
//   amt: string;
//   tid: number;
//   qty: number;
//   idx: number;
// };

// Type for the new Announcement event from TheCityCrier.sol
type AnnouncementEventData = {
  userwallet: string;
  eventID: number; // This is the key for the map
  prizetype: number; // This defines the actual prize type received
  prizecount: number; // Relevant for ERC1155
  prizeID: ethers.BigNumber; // Token ID (NFT) or Amount (ERC20/AVAX)
  prizeAddress: string; // Token/Collection address
};

// Extended type for display purposes, including fetched token info
type DisplayPrizeEventData = Partial<AnnouncementEventData> & {
  fetchedSymbol?: string;
  fetchedDecimals?: number;
};

// Log & TransactionReceipt types - keep if used by any transaction handling, otherwise can be pruned
interface Log {
  topics: string[]; data: string; transactionHash: string; logIndex: number; blockNumber: number; blockHash: string; address: string;
}
interface TransactionReceipt {
  to: string; from: string; contractAddress: string; transactionIndex: number; gasUsed: ethers.BigNumber; logsBloom: string; blockHash: string; transactionHash: string; logs: Log[]; blockNumber: number; confirmations: number; cumulativeGasUsed: ethers.BigNumber; effectiveGasPrice: ethers.BigNumber; status: number; type: number; byzantium: boolean;
}

// PrizeName type - adjusted for consistency
type PrizeName =
  | "Greed's Gift"
  | "Maw's Curse"
  | "Spoils of The Core" // Consolidates N/F versions for display name
  | "Sakura's Embrace"
  | "Siren's Call"
  | "Wrath's Intervention"
  | "Heretic's Honour"
  | "Fallen Fragments"
  | "An Unknown Encounter"; // Fallback

// Constants
const AURA_TOKEN = "0x3DB3aa87121d16407cFbbADf3f1c5b519182a399";
const HERESY_TOKEN = "0x432d38f83a50ec77c409d086e97448794cf76dcf";
const TOTAL_SUPPLY = ethers.utils.parseUnits("1000000000", 18); // 1 billion AURA
const WAVAX_TOKEN_ADDRESS_MAINNET = "0xB31f66AA3C1e785363F0875A1B74E27b85FD66c7"; // Added for Greed's Gift

// Frontend mapping based on backend EVENTS from index.js - REVISED
const FRONTEND_EVENTS_MAP: Record<number, {
  name: PrizeName;
  videoName: string;
  decimals?: number; // For ERC20/AVAX formatting
  symbol?: string; // Added for ERC20 symbol
}> = {
  // Based on user's provided backend EVENTS list
  1: { name: "Greed's Gift", videoName: "GreedsGift", decimals: 18, symbol: "WAVAX" }, // Type 1 (ERC20)
  2: { name: "Maw's Curse", videoName: "MawsCurse" }, // Type 0 (NONE)
  3: { name: "Spoils of The Core", videoName: "TheCore" }, // Type 3 (ERC721)
  4: { name: "Spoils of The Core", videoName: "TheCore" }, // Type 4 (ERC1155) - Assumed collection
  5: { name: "Sakura's Embrace", videoName: "SakurasEmbrace" }, // Type 3 (ERC721) - Assumed collection
  6: { name: "Siren's Call", videoName: "SirensCall", decimals: 18, symbol: "AURA" }, // Type 1 (ERC20)
  7: { name: "Wrath's Intervention", videoName: "WrathsIntervention", decimals: 18, symbol: "AURA" }, // Type 1 (ERC20)
  8: { name: "Fallen Fragments", videoName: "FallenFragments" }, // Type 3 (ERC721) - Needs verification of collection name if not 'Fragments'
  9: { name: "Heretic's Honour", videoName: "HereticsHonour", decimals: 18, symbol: "HERESY" } // Type 1 (ERC20) - Assumed HERESY based on tracking
  // Add more mappings here if the backend has more event IDs
};

// Helper function for formatting large numbers
const formatLargeNumber = (num: number): string => {
  const roundedNum = Math.round(num * 100) / 100;
  if (roundedNum >= 1000000000) return (roundedNum / 1000000000).toFixed(2) + 'B';
  if (roundedNum >= 1000000) return (roundedNum / 1000000).toFixed(2) + 'M';
  if (roundedNum >= 1000) return (roundedNum / 1000).toFixed(2) + 'K';
  return roundedNum.toFixed(2);
};

// UI Components (ProgressBar was tied to old explorationState, can be removed unless repurposed)
// const ProgressBar = ({current, max, label}: {current: number; max: number; label: string}): JSX.Element => { /* ... */ };

const LoadingIndicator = ({isActive}: {isActive: boolean}): JSX.Element | null => {
  if (!isActive) return null;
  return (
    <div className="loading-overlay">
      <div className="loading-spinner"><div className="spinner-inner"></div></div>
      <div className="loading-text fb">Loading...</div>
    </div>
  );
};

const Notification = ({ message, type, isVisible, onClose }: { message: string; type: 'success' | 'error' | 'info'; isVisible: boolean; onClose: () => void; }): JSX.Element | null => {
  useEffect(() => {
    if (!isVisible) return;
    const timer = setTimeout(() => onClose(), 5000);
    return () => clearTimeout(timer);
  }, [isVisible, onClose]);
  if (!isVisible) return null;
  return (
    <div className={`notification ${type}-notification`}>
      <div className="notification-content">
        <div className={`notification-icon ${type}-icon`}></div>
        <div className="notification-message">{message}</div>
      </div>
      <button className="notification-close" onClick={onClose}>×</button>
    </div>
  );
};

const RewardAnimation = ({ amount, isVisible, onComplete }: { amount: string; isVisible: boolean; onComplete: () => void; }): JSX.Element | null => {
  const [particles, setParticles] = useState<{id: number; x: number; y: number; size: number; speed: number}[]>([]);
  const [opacity, setOpacity] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    setOpacity(0);
    const fadeInTimer = setTimeout(() => setOpacity(1), 50);
    const newParticles = Array.from({length: 20}, (_, i) => ({ id: i, x: 50 + Math.random() * 15 - 7.5, y: 60 + Math.random() * 10, size: 6 + Math.random() * 8, speed: 0.6 + Math.random() * 0.8 }));
    setParticles(newParticles);
    const fadeOutTimer = setTimeout(() => setOpacity(0), 2400);
    const completeTimer = setTimeout(() => onComplete(), 3000);
    return () => { clearTimeout(fadeInTimer); clearTimeout(fadeOutTimer); clearTimeout(completeTimer); };
  }, [isVisible, onComplete]);
  if (!isVisible) return null;
  return (
    <div className="reward-animation-container" style={{ opacity: opacity, transition: 'opacity 0.6s ease-in-out' }}>
      <div className="reward-amount">{amount}</div>
      <div className="particles-container">
        {particles.map(p => <div key={p.id} className="particle" style={{ left: `${p.x}%`, bottom: `${p.y}%`, width: `${p.size}px`, height: `${p.size}px`, animationDuration: `${3 / p.speed}s` }} />)}
      </div>
    </div>
  );
};

const Tooltip = ({text, children}: {text: string; children: React.ReactNode}): JSX.Element => {
  return (
    <div className="tooltip-container">
      {children}
      <div className="tooltip-text">{text}</div>
    </div>
  );
};

// CSS for prize result display (defined at module level)
const prizeResultDisplayStyles = `
  .prize-result-display-overlay {
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    background-color: rgba(0,0,0,0.75);
    backdrop-filter: blur(5px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2000; 
  }
  .prize-result-display-content {
    background-color: #0a0a1a; 
    background-image: url('/src/styles/images/bg3.gif');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    padding: 30px;
    border-radius: 10px;
    border: 1px solid #00ffff;
    box-shadow: 0 0 20px rgba(0, 255, 255, 0.5);
    text-align: center;
    max-width: 500px;
    width: 90%;
  }
  .prize-result-display-content h3 {
    font-family: 'Another Danger', sans-serif;
    color: #00ffff;
    margin-top: 0;
    font-size: 2rem;
  }
  .prize-result-display-content p {
    margin-bottom: 20px;
    font-size: 1.1rem;
    color: white;
  }
  .prize-result-display-content .ccbutton {
    margin-top: 10px;
    padding: 10px 20px; /* Increased padding */
  }
`;

const App = (): JSX.Element => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/roleclaim" element={<RoleClaim />} />
        <Route path="/blubstaking" element={<BlubStaking />} />
        <Route path="/tokenswap" element={<TokenSwap />} />
      </Routes>
    </Router>
  );
};

const Home = (): JSX.Element => {
  const address = useAddress();
  const [ownedTokens, setOwnedTokens] = useState<NftBalance[]>([]);
  const [stakedTokens, setStakedTokens] = useState<BigNumber[]>([]);
  const [claimableRewards, setClaimableRewards] = useState<BigNumber>();
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false); // Default to false
  const initialLoadAttemptedForCurrentAddress = useRef(false); // Flag for initial load attempt

  const [auraAllowance, setAuraAllowance] = useState<BigNumber>(BigNumber.from(0));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [avaxBalance, setAvaxBalance] = useState<string>("0");
  const [currentVideo, setCurrentVideo] = useState<string | null>(null);
  const [isPlayingVideo, setIsPlayingVideo] = useState(false);
  const [currentPrizeName, setCurrentPrizeName] = useState<string | null>(null);
  // Update state types to use DisplayPrizeEventData
  const [currentPrizeDetails, setCurrentPrizeDetails] = useState<DisplayPrizeEventData[] | null>(null);
  const [prizeResultToDisplay, setPrizeResultToDisplay] = useState<DisplayPrizeEventData[] | null>(null);
  const [needsApproval, setNeedsApproval] = useState<boolean>(true);
  const [userDisplayName, setUserDisplayName] = useState<string | null>(null);
  const [circulatingSupply, setCirculatingSupply] = useState<string>("Loading...");
  const [burnedTokens, setBurnedTokens] = useState<string>("Loading...");
  const [videoVolume, setVideoVolume] = useState<number>(0.7);
  const [isVolumeSliderVisible, setIsVolumeSliderVisible] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [rewardAnimation, setRewardAnimation] = useState({ isVisible: false, amount: "0" });
  const [notification, setNotificationState] = useState<{ message: string; type: 'success' | 'error' | 'info'; isVisible: boolean; }>({ message: '', type: 'info', isVisible: false });
  const [isExploreHovered, setIsExploreHovered] = useState(false);

  const showNotification = (message: string, type: 'success' | 'error' | 'info') => setNotificationState({ message, type, isVisible: true });
  const hideNotification = () => setNotificationState(prev => ({ ...prev, isVisible: false }));
  const hideRewardAnimation = () => setRewardAnimation({ isVisible: false, amount: "0" });

  const distortionsContractAddress = "0x0a337be2ea71e3aea9c82d45b036ac6a6123b6d0";
  const stakingContractAddress = "0x51697170F78136c8d143B0013Cf5B229aDe70757";
  const theCityContractAddress = "0x4f1c9ddb905a2a56Aea1eb24E64E344b70500898";
  const theCityCrierContractAddress = "0xf14818AC38fCfd03bF4772F1c8559c11cC325E22";
  const auraContractAddress = AURA_TOKEN;
  const heresyContractAddress = HERESY_TOKEN;
  const roleClaimPortalAddress = "0x5463143F0E79DBaA602AebA78d6c31A433d6F96F";

  const { contract: distortionsContract } = useContract( distortionsContractAddress, "nft-drop" );
  const { contract: stakingContract } = useContract( stakingContractAddress, "custom" );
  const { contract: theCityContract } = useContract( theCityContractAddress, [ { "type": "function", "name": "tokenFeeAmount", "stateMutability": "view", "inputs": [], "outputs": [{ "name": "", "type": "uint256" }] }, { "type": "function", "name": "avaxFeeAmount", "stateMutability": "view", "inputs": [], "outputs": [{ "name": "", "type": "uint256" }] }, { "type": "function", "name": "explore", "stateMutability": "payable", "inputs": [], "outputs": [] } ] as const );
  const { contract: theCityCrierContract } = useContract( theCityCrierContractAddress, [ { "type": "event", "name": "Announcement", "inputs": [ { "name": "userwallet", "type": "address", "indexed": true }, { "name": "eventID", "type": "uint8", "indexed": false }, { "name": "prizetype", "type": "uint8", "indexed": false }, { "name": "prizecount", "type": "uint8", "indexed": false }, { "name": "prizeID", "type": "uint256", "indexed": false }, { "name": "prizeAddress", "type": "address", "indexed": false } ], "anonymous": false } ] as const );
  const { contract: auraContract } = useContract( auraContractAddress, "token" );
  const { contract: heresyContract } = useContract( heresyContractAddress, "token" );
  const { contract: roleClaimContract } = useContract( roleClaimPortalAddress, "custom" );
  
  const allContractsReady = useMemo(() => {
    return !!(distortionsContract && stakingContract && theCityContract && theCityCrierContract && auraContract && heresyContract && roleClaimContract);
  }, [distortionsContract, stakingContract, theCityContract, theCityCrierContract, auraContract, heresyContract, roleClaimContract]);

  const isWalletConnected = !!address;
  const hasTokenStaked = stakedTokens.length > 0;
  const { data: auraBalance } = useTokenBalance(auraContract, address);
  const [theCityTokenFee, setTheCityTokenFee] = useState<BigNumber>();
  const [theCityAvaxFee, setTheCityAvaxFee] = useState<BigNumber>();
  const { data: heresyBalance } = useTokenBalance(heresyContract, address);

  const STAKING_WALLET = "0x51697170F78136c8d143B0013Cf5B229aDe70757";
  const TREASURY_WALLET = "0x34Bf7e538BAA555053eC81704923A473ACF4b2e2";
  const DEAD_WALLET = "0x000000000000000000000000000000000000dEaD";

  const fetchAvaxBalance = async (): Promise<void> => {
    if (!address) return;
    try {
      const provider = new ethers.providers.Web3Provider((window as any).ethereum);
      const balance = await provider.getBalance(address);
      setAvaxBalance(ethers.utils.formatEther(balance));
    } catch (error) { console.error("Error fetching AVAX balance:", error); }
  };

  const calculateSupplies = async (): Promise<void> => {
    if (!auraContract || !theCityContractAddress) { 
      console.log("Contracts not ready for supply calc."); 
      setCirculatingSupply("Error"); setBurnedTokens("Error"); return; 
    }
    try {
      const rawStakingBalance = await auraContract.call("balanceOf", [STAKING_WALLET]);
      const rawTreasuryBalance = await auraContract.call("balanceOf", [TREASURY_WALLET]);
      const rawBurnedBalance = await auraContract.call("balanceOf", [DEAD_WALLET]);
      const rawCityContractBalance = await auraContract.call("balanceOf", [theCityContractAddress]);

      const stakingBalance = Array.isArray(rawStakingBalance) ? rawStakingBalance[0] : rawStakingBalance;
      const treasuryBalance = Array.isArray(rawTreasuryBalance) ? rawTreasuryBalance[0] : rawTreasuryBalance;
      const burnedBalance = Array.isArray(rawBurnedBalance) ? rawBurnedBalance[0] : rawBurnedBalance;
      const cityContractBalance = Array.isArray(rawCityContractBalance) ? rawCityContractBalance[0] : rawCityContractBalance;

      if (!stakingBalance || !treasuryBalance || !burnedBalance || !cityContractBalance) {
        console.error("One or more balances are undefined after call.");
        setCirculatingSupply("Error"); setBurnedTokens("Error"); return;
      }

      const excludedBalance = stakingBalance.add(treasuryBalance).add(burnedBalance).add(cityContractBalance);
      const circulating = TOTAL_SUPPLY.sub(excludedBalance); 
      setCirculatingSupply(formatLargeNumber(Number(ethers.utils.formatUnits(circulating, 18)))); 
      setBurnedTokens(formatLargeNumber(Number(ethers.utils.formatUnits(burnedBalance, 18))));
    } catch (error) { 
        console.error("Error calculating supplies:", error); 
        setCirculatingSupply("Error"); setBurnedTokens("Error"); 
    }
  };

  const refreshTokenBalances = async (): Promise<void> => { 
      if(!address || !distortionsContractAddress) return;
      try {
        const response = await fetch(`https://glacier-api.avax.network/v1/chains/43114/addresses/${address}/balances:listErc721?pageSize=10&contractAddress=${distortionsContractAddress}`);
        const data = await response.json();
        if (data && data.erc721TokenBalances) {
            const tokenBalances: NftBalance[] = data.erc721TokenBalances.map((item: any) => ({ tokenId: item.tokenId }));
            setOwnedTokens(tokenBalances);
        } else { setOwnedTokens([]); }
      } catch (e) { console.error("Failed to fetch owned NFTs:", e); setOwnedTokens([]); }
  };
  const loadStakedTokens = async (): Promise<void> => { 
    if(!stakingContract || !address) { return; }
    try {
        const stakeInfo = await stakingContract.call("getStakeInfo", [address]);
        if (Array.isArray(stakeInfo) && stakeInfo.length > 0) setStakedTokens(stakeInfo[0]);
        else setStakedTokens([]);
    } catch(e) { console.error("Failed to load staked tokens:", e); setStakedTokens([]); }
  };
  const loadClaimableRewards = async (): Promise<void> => { 
    if(!stakingContract || !address) { return; }
    try {
        const stakeInfo = await stakingContract.call("getStakeInfo", [address]);
        if (Array.isArray(stakeInfo) && stakeInfo.length > 1) setClaimableRewards(stakeInfo[1]);
        else setClaimableRewards(undefined);
    } catch(e) { console.error("Failed to load claimable rewards:", e); setClaimableRewards(undefined); }
  };
  const loadTheCityFees = async (): Promise<void> => {
    if (!theCityContract) { return; }
    try {
      const tokenFee = await theCityContract.call("tokenFeeAmount");
      const avaxFee = await theCityContract.call("avaxFeeAmount");
      setTheCityTokenFee(tokenFee); setTheCityAvaxFee(avaxFee);
    } catch (error) { console.error("Failed to load fees from TheCity contract:", error); }
  };

  const refreshAllData = async (): Promise<void> => {
    if (!address) return;
    try {
      await loadTheCityFeesCallback();
      await fetchAvaxBalanceCallback();
      await calculateSuppliesCallback();
      await refreshTokenBalancesCallback();
      await loadStakedTokensCallback();
      await loadClaimableRewardsCallback();
    } catch (error) {
      console.error("Generic error in refreshAllData wrapper:", error);
    } finally {
      console.log("refreshAllData (data fetching part) finished.");
    }
  };

  const fetchAvaxBalanceCallback = useCallback(fetchAvaxBalance, [address]);
  const calculateSuppliesCallback = useCallback(calculateSupplies, [auraContract, theCityContractAddress, STAKING_WALLET, TREASURY_WALLET, DEAD_WALLET]);
  const refreshTokenBalancesCallback = useCallback(refreshTokenBalances, [address, distortionsContractAddress]);
  const loadStakedTokensCallback = useCallback(loadStakedTokens, [stakingContract, address]);
  const loadClaimableRewardsCallback = useCallback(loadClaimableRewards, [stakingContract, address]);
  const loadTheCityFeesCallback = useCallback(loadTheCityFees, [theCityContract]);

  const refreshAllDataCallback = useCallback(async (): Promise<void> => {
    if (!address) return;
    try {
      await loadTheCityFeesCallback(); 
      await fetchAvaxBalanceCallback(); 
      await calculateSuppliesCallback(); 
      
      if (stakingContract && distortionsContract) {
        await refreshTokenBalancesCallback(); 
        await loadStakedTokensCallback(); 
        await loadClaimableRewardsCallback(); 
      }
    } catch (error) {
      console.error("Generic error in refreshAllData wrapper:", error);
    } finally {
      console.log("refreshAllData (data fetching part) finished.");
    }
  }, [
    address, 
    theCityContract, 
    auraContract, 
    theCityContractAddress, 
    stakingContract, 
    distortionsContract,
    loadTheCityFeesCallback,
    fetchAvaxBalanceCallback,
    calculateSuppliesCallback,
    refreshTokenBalancesCallback,
    loadStakedTokensCallback,
    loadClaimableRewardsCallback
  ]);

  const handleApproval = async (): Promise<void> => {
    if (!auraContract || !theCityTokenFee || !address || !theCityContractAddress) {
      showNotification("Approval setup error.", "error"); return;
    }
    setIsLoading(true);
    showNotification("Approving AURA tokens for The City...", "info");
    try {
      await auraContract.call("approve", [theCityContractAddress, theCityTokenFee]);
      await new Promise(resolve => setTimeout(resolve, 1000)); 
      const newAllowanceResult = await auraContract.call("allowance", [address, theCityContractAddress]);
      const newAllowance = Array.isArray(newAllowanceResult) ? newAllowanceResult[0] : newAllowanceResult;
      setAuraAllowance(newAllowance);
      setNeedsApproval(newAllowance.lt(theCityTokenFee));
      showNotification("AURA tokens approved successfully!", "success");
    } catch (error: any) {
      showNotification(`Approval failed: ${error.message || "Unknown error"}`, "error");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleExploreOrClaim = async (): Promise<void> => {
    setErrorMessage(null);
    if (!address || !theCityContract || !auraContract || !theCityTokenFee || !theCityAvaxFee) {
      showNotification("Initialization error or fees not loaded. Please refresh.", "error"); return;
    }
    if (auraBalance && theCityTokenFee && auraBalance.value.lt(theCityTokenFee)) {
      showNotification("Not enough AURA to explore.", "error"); return;
    }
    if (needsApproval) {
      await handleApproval(); 
      return; 
    }
    showNotification("Preparing to explore The City...", "info");
    setIsLoading(true);
    const exploreTimeout = setTimeout(() => {
      if (isLoading) { 
        setIsLoading(false);
        showNotification("Exploration timed out waiting for The Crier. Please check your transaction.", "error");
      }
    }, 60000); 
    try {
      const exploreTx = await theCityContract.call("explore", [], { value: theCityAvaxFee });
      showNotification("Exploration transaction sent! Awaiting The Crier's announcement...", "success");
      console.log("Explore transaction submitted:", exploreTx.hash);
    } catch (exploreError: any) {
      console.error("TheCity Explore transaction error:", exploreError);
      showNotification(`Exploration failed: ${exploreError.message || "Unknown transaction error"}`, "error");
      setIsLoading(false);
      clearTimeout(exploreTimeout);
    }
  };

  const handleStake = async (id: string): Promise<void> => {
    if (!stakingContract || !address || !distortionsContract) {
      showNotification("Staking service not ready.", "error"); return;
    }
    setIsLoading(true);
    showNotification("Staking NFT...", "info");
    try {
      const isApproved = await distortionsContract.call("isApprovedForAll", [address, stakingContractAddress]);
      if (!isApproved) {
        showNotification("Approving collection for staking...", "info");
        await distortionsContract.call("setApprovalForAll", [stakingContractAddress, true]);
      }
      await stakingContract.call("stake", [[id]]);
      showNotification("NFT Staked!", "success");
      await refreshAllDataCallback();
    } catch (e: any) { 
      showNotification(`Stake failed: ${e.message || "Unknown error"}`, "error"); 
    } finally { setIsLoading(false); }
  };
  const handleUnstake = async (id: string): Promise<void> => { 
    if (!stakingContract || !address) {
      showNotification("Staking service not ready.", "error"); return;
    }
    setIsLoading(true);
    showNotification("Unstaking NFT...", "info");
    try {
      await stakingContract.call("withdraw", [[id]]);
      showNotification("NFT Unstaked!", "success");
      await refreshAllDataCallback();
    } catch (e: any) { 
      showNotification(`Unstake failed: ${e.message || "Unknown error"}`, "error"); 
    } finally { setIsLoading(false); }
  };
  const handleClaimRewards = async (): Promise<void> => { 
    if (!stakingContract || !address || !claimableRewards || claimableRewards.isZero()) {
        showNotification("No rewards to claim or staking service not ready.", "info"); return;
    }
    setIsLoading(true);
    showNotification("Claiming AURA rewards...", "info");
    try {
        const formattedRewards = formatClaimableRewards();
        await stakingContract.call("claimRewards");
        setRewardAnimation({ isVisible: true, amount: `+${formattedRewards} AURA` });
        showNotification("Rewards Claimed!", "success");
        await loadClaimableRewardsCallback();
        await fetchAvaxBalanceCallback();
    } catch (e: any) { 
        showNotification(`Claim rewards failed: ${e.message || "Unknown error"}`, "error"); 
    } finally { setIsLoading(false); }
  };
  const formatClaimableRewards = () => { return claimableRewards ? formatLargeNumber(Number(ethers.utils.formatUnits(claimableRewards, 18))) : "0.00"; };

  useEffect(() => { /* Fetch display name */ 
    const fetchDisplayNameFn = async () => {
      if (!address || !roleClaimContract) return;
      try {
        const name = await roleClaimContract.call("readDisplayName", [address]);
        if (name && name !== "") {
          setUserDisplayName(name);
        } else {
          setUserDisplayName(null);
        }
      } catch (error) { 
        console.error("Error fetching display name:", error); 
        setUserDisplayName(null);
      }
    };
    if(roleClaimContract && address) {
      fetchDisplayNameFn();
    } else if (!address) {
      setUserDisplayName(null);
    }
  }, [address, roleClaimContract]);

  // This useEffect manages setting isInitializing to true when a new address connects
  // and an initial load hasn't been attempted for this session yet.
  // It also handles cleanup on disconnect.
  useEffect(() => {
    if (address) {
      if (!initialLoadAttemptedForCurrentAddress.current) {
        console.log(`Address ${address} available. Initial load not yet attempted for this session. Setting isInitializing = true.`);
        setIsInitializing(true);
        // The flag initialLoadAttemptedForCurrentAddress.current will be set to true
        // by the data loading useEffect once it actually starts the fetch.
      }
    } else {
      // Wallet disconnected
      console.log("Wallet disconnected. Resetting initialLoadAttempted flag and ensuring isInitializing is false.");
      initialLoadAttemptedForCurrentAddress.current = false;
      setIsInitializing(false);
      // Reset other user-specific states
      setOwnedTokens([]);
      setStakedTokens([]);
      setClaimableRewards(undefined);
      setAuraAllowance(BigNumber.from(0));
      setAvaxBalance("0");
      setCurrentVideo(null);
      setIsPlayingVideo(false);
      setCurrentPrizeName(null);
      setCurrentPrizeDetails(null);
      setPrizeResultToDisplay(null);
      setNeedsApproval(true);
      setTheCityTokenFee(undefined);
      setTheCityAvaxFee(undefined);
      setUserDisplayName(null); 
    }
  }, [address]);

  // This useEffect performs the actual data loading when conditions are met.
  useEffect(() => {
    // console.log(`Data loading useEffect triggered. Address: ${!!address}, AllContractsReady: ${allContractsReady}, isInitializing: ${isInitializing}`);

    const performInitialLoad = async () => {
      if (address && allContractsReady && isInitializing) {
        console.log("Performing initial data load: address, allContractsReady, and isInitializing are true.");
        initialLoadAttemptedForCurrentAddress.current = true; 

        try {
          await refreshAllDataCallback();
          console.log("Initial data refresh complete (from performInitialLoad).");
        } catch (error) {
          console.error("Error during initial data refresh (from performInitialLoad):", error);
        } finally {
          console.log("Initial data load attempt finished (from performInitialLoad). Setting isInitializing = false.");
          setIsInitializing(false);
        }
      } else if (address && !allContractsReady && isInitializing) {
        console.log("Initial load: Waiting for allContractsReady. isInitializing remains true.");
      }
    };

    if (isInitializing && address) { 
        performInitialLoad();
    } else if (!address && isInitializing) {
        console.log("Address disconnected while isInitializing was true. Forcing isInitializing to false.");
        setIsInitializing(false);
    }
  }, [address, allContractsReady, isInitializing, refreshAllDataCallback]);

  useEffect(() => { /* Check AURA allowance */
    const checkAuraAllowance = async () => {
      if (!address || !auraContract || !theCityTokenFee || !theCityContract || !allContractsReady) return;
      try {
        const allowanceResult = await auraContract.call("allowance", [address, theCityContractAddress]);
        const allowance = Array.isArray(allowanceResult) ? allowanceResult[0] : allowanceResult;
        if (allowance && theCityTokenFee) {
             setAuraAllowance(allowance);
             setNeedsApproval(allowance.lt(theCityTokenFee));
        } else {
            setNeedsApproval(true);
        }
      } catch (error) {
        console.error("Error fetching AURA allowance:", error);
        setAuraAllowance(BigNumber.from(0)); setNeedsApproval(true);
      }
    };
    if (theCityTokenFee) {
        checkAuraAllowance();
    }
  }, [address, auraContract, theCityTokenFee, theCityContract, theCityContractAddress, allContractsReady]);

  // Ref to manage collecting multiple events for Sakura's Embrace
  const sakuraEventBuffer = useRef<Partial<AnnouncementEventData>[]>([]);
  const sakuraTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Minimal ERC20 ABI for fetching symbol and decimals
  const ERC20_ABI = [
    {
      "constant": true,
      "inputs": [],
      "name": "symbol",
      "outputs": [{ "name": "", "type": "string" }],
      "payable": false,
      "stateMutability": "view",
      "type": "function"
    },
    {
      "constant": true,
      "inputs": [],
      "name": "decimals",
      "outputs": [{ "name": "", "type": "uint8" }],
      "payable": false,
      "stateMutability": "view",
      "type": "function"
    }
  ];

  useEffect(() => { /* Event Listener for TheCityCrier */
    if (!theCityCrierContract || !address || !theCityCrierContract.events) return;

    const SAKURA_EVENT_ID = 5;
    const SAKURA_TIMEOUT_MS = 3000; // 1 second timeout to collect both events

    // Modified processCollectedEvents to be async and fetch token data
    const processCollectedEvents = async (eventsToProcess: Partial<AnnouncementEventData>[]) => {
      if (eventsToProcess.length === 0) return;

      console.log(`Processing ${eventsToProcess.length} collected event(s).`);
      // setIsLoading(false); // Stop loading indicator - MOVED to after data fetching

      const provider = new ethers.providers.Web3Provider((window as any).ethereum);

      // Augment events with fetched token data if they are ERC20
      const augmentedEvents: DisplayPrizeEventData[] = await Promise.all(
        eventsToProcess.map(async (event) => {
          const displayEvent: DisplayPrizeEventData = { ...event }; // Copy to new type
          if (event.prizetype === 1 && event.prizeAddress && event.prizeID) { // ERC20
            try {
              const tokenContract = new ethers.Contract(event.prizeAddress, ERC20_ABI, provider);
              const [symbol, decimals] = await Promise.all([
                tokenContract.symbol(),
                tokenContract.decimals()
              ]);
              displayEvent.fetchedSymbol = symbol;
              displayEvent.fetchedDecimals = decimals;
              console.log(`Fetched for ${event.prizeAddress}: Symbol=${symbol}, Decimals=${decimals}`);
            } catch (e) {
              console.error(`Failed to fetch token data for ${event.prizeAddress}:`, e);
              // fetchedSymbol and fetchedDecimals will remain undefined
            }
          }
          return displayEvent;
        })
      );
      
      setIsLoading(false); // Stop loading indicator AFTER data fetching

      const firstEvent = augmentedEvents[0]; // Use the first augmented event
      setCurrentPrizeDetails(augmentedEvents); // Store all augmented events

      const eventMapping = FRONTEND_EVENTS_MAP[firstEvent.eventID!];
      let newVideoPath: string;
      let newPrizeName: string;

      if (eventMapping) {
        newPrizeName = eventMapping.name;
        newVideoPath = `/videos/${eventMapping.videoName}.mp4`;
      } else {
        newPrizeName = "An Unknown Encounter";
        newVideoPath = "/videos/MawsCurse.mp4"; // Fallback video
      }

      setCurrentPrizeName(newPrizeName); // Update name regardless

      // If not currently playing a video OR if the new video is different from the current one
      if (!isPlayingVideo || (currentVideo !== newVideoPath)) {
        setCurrentVideo(newVideoPath);
        setIsPlayingVideo(true);
      } else {
        // It's the same video and isPlayingVideo is true. Do nothing to avoid restart.
        console.log("Video already playing and is the same as the new event. Not restarting.");
      }
      refreshAllDataCallback(); // Refresh data after prize event(s) are processed
    };

    const eventName = "Announcement";
    const listener = (event: { data: AnnouncementEventData, eventName: string }) => {
      if (event.eventName === eventName) {
        const eventData = event.data;
        if (eventData.userwallet && eventData.userwallet.toLowerCase() === address.toLowerCase()) {
          console.log("Matched Announcement for user:", eventData);

          // Create a modifiable copy of eventData
          const correctedEventData = { ...eventData };

          // Handle specific event ID prize type corrections if backend sends type 2 for NFTs
          if (correctedEventData.eventID === SAKURA_EVENT_ID && correctedEventData.prizetype === 2) { // Sakura (ID 5)
            console.log("Sakura's Embrace (event ID 5) received with prizetype 2. Correcting to 3 (ERC721).");
            correctedEventData.prizetype = 3;
          } else if (correctedEventData.eventID === 3 && correctedEventData.prizetype === 2) { // Spoils N (ID 3)
            console.log("Spoils of The Core N (event ID 3) received with prizetype 2. Correcting to 3 (ERC721).");
            correctedEventData.prizetype = 3;
          } else if (correctedEventData.eventID === 4 && correctedEventData.prizetype === 2) { // Spoils F (ID 4)
            console.log("Spoils of The Core F (event ID 4) received with prizetype 2. Correcting to 4 (ERC1155).");
            correctedEventData.prizetype = 4;
          } else if (correctedEventData.eventID === 8 && correctedEventData.prizetype === 2) { // Fallen Fragments (ID 8)
            console.log("Fallen Fragments (event ID 8) received with prizetype 2. Correcting to 3 (ERC721).");
            correctedEventData.prizetype = 3; // Fallen Fragments are ERC721
          }
          // Add more else if blocks here if other events need prizetype correction

          // Now process using the correctedEventData
          if (correctedEventData.eventID === SAKURA_EVENT_ID) {
             // Clear any existing timer if a new Sakura event comes in quickly
             if (sakuraTimerRef.current) {
                clearTimeout(sakuraTimerRef.current);
                sakuraTimerRef.current = null;
             }

             sakuraEventBuffer.current.push(correctedEventData); // Use corrected data
             console.log(`Sakura event ${sakuraEventBuffer.current.length} received.`);

             // If we've got 2, process immediately
             if (sakuraEventBuffer.current.length === 2) {
                 console.log("Processing 2 Sakura events.");
                 processCollectedEvents([...sakuraEventBuffer.current]).catch(console.error); // Call async fn
                 sakuraEventBuffer.current = []; // Reset buffer
             } else {
                 // If only 1, set a timer to wait for the second
                 console.log("Setting timer for second Sakura event...");
                 sakuraTimerRef.current = setTimeout(() => {
                     console.log("Sakura timer expired. Processing collected events:", sakuraEventBuffer.current);
                     processCollectedEvents([...sakuraEventBuffer.current]).catch(console.error); // Call async fn
                     sakuraEventBuffer.current = []; // Reset buffer
                     sakuraTimerRef.current = null;
                 }, SAKURA_TIMEOUT_MS);
             }
          } else {
            // Handle all other events immediately (using correctedEventData which would be same as eventData if not Sakura)
            console.log(`Processing non-Sakura event ID: ${correctedEventData.eventID}`);
            // Ensure any pending Sakura timer is cleared if a different event arrives
            if (sakuraTimerRef.current) {
                 clearTimeout(sakuraTimerRef.current);
                 sakuraTimerRef.current = null;
                 if(sakuraEventBuffer.current.length > 0) {
                    console.warn("Received non-Sakura event while waiting for second Sakura event. Processing buffered Sakura event(s).");
                    processCollectedEvents([...sakuraEventBuffer.current]).catch(console.error); // Call async fn
                    sakuraEventBuffer.current = []; // Reset buffer
                 }
            }
            processCollectedEvents([correctedEventData]).catch(console.error); // Call async fn & catch errors
          }
        }
      }
    };

    const unsubscribe = theCityCrierContract.events.listenToAllEvents(listener);
    return () => { 
      unsubscribe(); 
      // Clear timer on cleanup
      if (sakuraTimerRef.current) {
        clearTimeout(sakuraTimerRef.current);
      }
      sakuraEventBuffer.current = []; // Clear buffer on cleanup
    };
  }, [theCityCrierContract, address, refreshAllDataCallback]); // Removed ERC20_ABI from deps as it's constant

  useEffect(() => { /* Video volume */
    if (videoRef.current) videoRef.current.volume = videoVolume; 
  }, [videoVolume, isPlayingVideo]);

  // SVG Icon components should be defined outside Home if they don't need its scope
  // Updated Icons: Bigger (32x32) and Brighter (fill: #00ffff)
  const DiscordIcon = () => ( <svg viewBox="0 0 245 240" width="32" height="32" fill="#00ffff"> <path d="M104.4 103.9c-5.7 0-10.2 5-10.2 11.1s4.6 11.1 10.2 11.1c5.7 0 10.2-5 10.2-11.1.1-6.1-4.5-11.1-10.2-11.1zM140.9 103.9c-5.7 0-10.2 5-10.2 11.1s4.6 11.1 10.2 11.1c5.7 0 10.2-5 10.2-11.1s-4.5-11.1-10.2-11.1z"/> <path d="M189.5 20h-134C44.2 20 35 29.2 35 40.6v135.2c0 11.4 9.2 20.6 20.5 20.6h113.4l-5.3-18.5 12.8 11.9 12.1 11.2 21.5 19V40.6c0-11.4-9.2-20.6-20.5-20.6zm-38.6 130.6s-3.6-4.3-6.6-8.1c13.1-3.7 18.1-11.9 18.1-11.9-4.1 2.7-8 4.6-11.5 5.9-5 2.1-9.8 3.5-14.5 4.3-9.6 1.8-18.4 1.3-25.9-.1-5.7-1.1-10.6-2.7-14.7-4.3-2.3-.9-4.8-2-7.3-3.4-.3-.2-.6-.3-.9-.5-.2-.1-.3-.2-.4-.3-1.8-1-2.8-1.7-2.8-1.7s4.8 8 17.5 11.8c-3 3.8-6.7 8.3-6.7 8.3-22.1-.7-30.5-15.2-30.5-15.2 0-32.2 14.4-58.3 14.4-58.3 14.4-10.8 28.7-10.8 28.7-10.8l1 1.2c-18 5.2-26.3 13.1-26.3 13.1s2.2-1.2 5.9-2.9c10.7-4.7 19.2-6 22.7-6.3.6-.1 1.1-.2 1.7-.2 6.1-.8 13-1 20.2-.2 9.5 1.1 19.7 3.9 30.1 9.6 0 0-7.9-7.5-24.9-12.7l1.4-1.6s14.3 0 28.7 10.8c0 0 14.4 26.1 14.4 58.3 0 0-8.5 14.5-30.6 15.2z"/> </svg> );
  const UserIcon = () => ( <svg viewBox="0 0 24 24" width="32" height="32" fill="#00ffff"> <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" /> </svg> );
  const ExternalLinkIcon = () => ( <svg viewBox="0 0 24 24" width="32" height="32" fill="#00ffff"> <path d="M18 19H6c-.55 0-1-.45-1-1V6c0-.55.45-1 1-1h5c.55 0 1-.45 1-1s-.45-1-1-1H5c-1.11 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-6c0-.55-.45-1-1-1s-1 .45-1 1v5c0 .55-.45 1-1 1zM14 4c0 .55.45 1 1 1h2.59l-9.13 9.13c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0L19 6.41V9c0 .55.45 1 1 1s1-.45 1-1V4c0-.55-.45-1-1-1h-5c-.55 0-1 .45-1 1z" /> </svg> );
  const CoinsIcon = () => ( <svg viewBox="0 0 24 24" width="32" height="32" fill="#00ffff"> <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z" /> </svg> );
  const ExchangeIcon = () => ( <svg viewBox="0 0 24 24" width="32" height="32" fill="#00ffff"> <path d="M6.99 11L3 15l3.99 4v-3H14v-2H6.99v-3zM21 9l-3.99-4v3H10v2h7.01v3L21 9z" /> </svg> );
  const XLogoIcon = () => ( <svg viewBox="0 0 24 24" width="32" height="32" fill="#00ffff" xmlns="http://www.w3.org/2000/svg"> <path d="M18.6941 2.01603H22.6701L14.4961 11.349L23.6861 21.984H15.6431L9.90212 14.83L3.50212 21.984H-0.474884L8.10812 12.171L-0.0728812 2.01603H8.21412L13.5001 8.68803L18.6941 2.01603ZM16.6811 19.879H18.0541L6.07012 4.01403H4.58712L16.6811 19.879Z"/> </svg> );
  // Add ClothingIcon SVG
  const ClothingIcon = () => (
    <svg viewBox="0 0 24 24" width="32" height="32" fill="#00ffff" xmlns="http://www.w3.org/2000/svg">
      <path d="M16.472 3.21c-.41-.13-.85-.02-1.14.29l-3.33 3.54-3.33-3.54c-.29-.31-.73-.42-1.14-.29l-5.01 1.6c-.41.13-.7.52-.7.95v2.13c0 .41.25.78.63.93l2.37.95v10.13c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V9.76l2.37-.95c.38-.15.63-.52.63-.93V5.76c0-.43-.29-.82-.7-.95l-5.01-1.6zm-8.47 2.13l3.33 3.54c.19.2.51.2.7 0l3.33-3.54 4.3 1.37v1.01l-2.37.95c-.38.15-.63.52-.63.93v10.13c0 .55-.45 1-1 1H7c-.55 0-1-.45-1-1V9.76c0-.41-.25-.78-.63-.93l-2.37-.95v-1.01l4.3-1.37z"/>
    </svg>
  );

  // Define a component to display ERC20 prize details
  const Erc20PrizeDisplayItem = ({ prizeAddress, prizeAmount, defaultDecimals }: { prizeAddress: string, prizeAmount: ethers.BigNumber, defaultDecimals?: number }) => {
    const { contract: tokenContract } = useContract(prizeAddress, "token");
    const [tokenSymbol, setTokenSymbol] = useState<string | null>(null);
    const [tokenDecimals, setTokenDecimals] = useState<number | null>(null);
    const [isLoadingData, setIsLoadingData] = useState(true);

    useEffect(() => {
      const fetchTokenData = async () => {
        if (!tokenContract) {
          setIsLoadingData(false);
          return;
        }
        setIsLoadingData(true);
        try {
          // const info = await tokenContract.erc20.info(); // Previous attempt
          const tokenData = await tokenContract.erc20.get(); // Fetch all ERC20 data
          setTokenSymbol(tokenData.symbol);
          setTokenDecimals(tokenData.decimals);
        } catch (error) {
          console.error("Error fetching token data for contract:", prizeAddress, error);
          if (prizeAddress.toLowerCase() === WAVAX_TOKEN_ADDRESS_MAINNET.toLowerCase()) {
            setTokenSymbol("WAVAX");
            setTokenDecimals(18);
          } else if (prizeAddress.toLowerCase() === AURA_TOKEN.toLowerCase()) {
            setTokenSymbol("AURA");
            setTokenDecimals(18);
          } else if (prizeAddress.toLowerCase() === HERESY_TOKEN.toLowerCase()) {
            setTokenSymbol("HERESY");
            setTokenDecimals(18);
          } else {
            setTokenSymbol("tokens"); 
            setTokenDecimals(defaultDecimals ?? 18); 
          }
        } finally {
          setIsLoadingData(false);
        }
      };
      fetchTokenData();
    }, [tokenContract, prizeAddress, defaultDecimals]);

    if (isLoadingData) return <div className="fb">Loading token info...</div>;
    
    const decimalsToUseInItem = tokenDecimals ?? defaultDecimals ?? 18;
    let formattedAmountInItem = "Error"; // Default to error string
    const displaySymbolInItem = tokenSymbol || (prizeAddress.toLowerCase() === WAVAX_TOKEN_ADDRESS_MAINNET.toLowerCase() ? "WAVAX" : "tokens");

    try {
      // Ensure prizeAmount is a BigNumber before formatting
      if (ethers.BigNumber.isBigNumber(prizeAmount)) {
        formattedAmountInItem = ethers.utils.formatUnits(prizeAmount, decimalsToUseInItem);
        const numAmount = Number(formattedAmountInItem);
        if (numAmount >= 1000) {
          formattedAmountInItem = formatLargeNumber(numAmount);
        } else if (Number.isInteger(numAmount)) {
          formattedAmountInItem = numAmount.toString();
        } else {
          // Format to 2 decimal places, but remove .00 if it's a whole number after rounding
          const fixedAmount = numAmount.toFixed(2);
          formattedAmountInItem = parseFloat(fixedAmount).toString();
        }
      } else {
        // Handle cases where prizeAmount might not be a BigNumber (e.g. if event data is malformed)
        console.error("prizeAmount is not a BigNumber:", prizeAmount);
        formattedAmountInItem = "Invalid amount";
      }
    } catch (formatError) { // Changed 'e' to 'formatError' for clarity
      console.error("Error formatting ERC20 amount:", prizeAmount?.toString(), "with decimals", decimalsToUseInItem, formatError);
      // Fallback to unformatted amount if formatting fails
       formattedAmountInItem = prizeAmount?.toString() ?? "Error";
    }

    return (
      <p className="fb prize-erc20-details">
        {`Received: ${formattedAmountInItem} ${displaySymbolInItem}`}
      </p>
    );
  };

  // Define a component to display NFT metadata
  const NftDisplay = ({ contractAddress, tokenId }: { contractAddress: string, tokenId: ethers.BigNumber }) => {
    const { contract } = useContract(contractAddress);
    // Fetch contract metadata (like collection name)
    const { data: contractMetadata, isLoading: isLoadingContractMetadata } = useContractMetadata(contract);
    // Ensure tokenId is converted to string for useNFT hook
    const { data: nft, isLoading: isLoadingNFT, error } = useNFT(contract, tokenId.toString());

    if (isLoadingNFT || isLoadingContractMetadata) return <div className="fb">Loading NFT...</div>;
    // Basic error display, can be improved
    // Check for metadata presence as well
    if (error || !nft || !nft.metadata) return <div className="fb">NFT details unavailable.</div>;

    // Determine the image URL, handling IPFS URIs
    const imageUrl = nft.metadata.image
      ? nft.metadata.image.startsWith("ipfs://") 
        ? `https://gateway.pinata.cloud/ipfs/${nft.metadata.image.substring(7)}` 
        : nft.metadata.image
      : undefined; // Handle cases where image might be missing

    return (
      <div className="nft-prize-display"> 
        {imageUrl && (
          <img
            src={imageUrl}
            alt={nft.metadata.name?.toString() ?? `NFT ${tokenId.toString()}`}
            className="prize-nft-image" // Add CSS for this class: e.g., max-width: 100px; border-radius: 5px; margin-bottom: 5px;
          />
        )}
        {/* Display NFT name */}
        <p className="fb prize-nft-name">{nft.metadata.name ?? `Token ID: ${tokenId.toString()}`}</p>
        {/* Display Collection name if available */}
        {contractMetadata?.name && (
           <p className="fb prize-collection-name">({contractMetadata.name})</p>
        )}
      </div>
    );
  };

  // REVISED formatPrizeForDisplay function
  const formatPrizeForDisplay = (detailsArray: DisplayPrizeEventData[] | null): string => {
    if (!detailsArray || detailsArray.length === 0) {
      return "Encounter details are unclear.";
    }

    const firstEvent = detailsArray[0];
    const eventID = firstEvent.eventID;
    const mapping = eventID !== undefined ? FRONTEND_EVENTS_MAP[eventID] : undefined;
    const eventName = mapping ? mapping.name : eventID !== undefined ? `Event ID ${eventID}` : "Unknown Event";
    const baseText = `You encountered ${eventName}!`;

    let nftCount = 0;
    const otherPrizeStrings: string[] = [];

    detailsArray.forEach(details => {
      const prizeType = details.prizetype;
      const prizeAmountOrId = details.prizeID; // This is ethers.BigNumber | undefined
      const prizeAddr = details.prizeAddress; // Available for logging or specific checks if needed
      const prizeCountFromEvent = details.prizecount; // This is number | undefined (for ERC1155 quantity)
      
      // Get itemMapping based on the eventID of the *current* detail for general event info like name
      const itemMapping = details.eventID !== undefined ? FRONTEND_EVENTS_MAP[details.eventID] : undefined;

      if (prizeType === 3) { // ERC721
        nftCount++;
      } else if (prizeType === 4) { // ERC1155
        nftCount += prizeCountFromEvent && prizeCountFromEvent > 0 ? prizeCountFromEvent : 1;
      } else if (prizeType === 0) { // NONE
        otherPrizeStrings.push("You received nothing.");
      } else if (prizeAmountOrId === undefined) { // If ERC20/AVAX/Other but no ID/Amount
        otherPrizeStrings.push("Prize details missing for an item.");
      } else if (prizeType === 1) { // ERC20
        // Prioritize fetched data, then check actual token address, then itemMapping, then defaults
        const decimals = details.fetchedDecimals ?? itemMapping?.decimals ?? 18;
        
        // Determine symbol: prioritize fetched, then check actual address, then event mapping, then default
        let symbol = details.fetchedSymbol;
        if (!symbol && prizeAddr) {
          // Check actual token address against known tokens
          if (prizeAddr.toLowerCase() === AURA_TOKEN.toLowerCase()) {
            symbol = "AURA";
          } else if (prizeAddr.toLowerCase() === HERESY_TOKEN.toLowerCase()) {
            symbol = "HERESY";
          } else if (prizeAddr.toLowerCase() === WAVAX_TOKEN_ADDRESS_MAINNET.toLowerCase()) {
            symbol = "WAVAX";
          } else {
            // Only use event mapping as last resort before "tokens"
            symbol = itemMapping?.symbol ?? "tokens";
          }
        } else if (!symbol) {
          symbol = itemMapping?.symbol ?? "tokens";
        }

        let formattedAmount = "Error";
        try {
          if (ethers.BigNumber.isBigNumber(prizeAmountOrId)) {
            formattedAmount = ethers.utils.formatUnits(prizeAmountOrId, decimals);
            const numAmount = Number(formattedAmount);
            if (numAmount >= 1000000) { 
              formattedAmount = formatLargeNumber(numAmount);
            } else if (Number.isInteger(numAmount)) {
              formattedAmount = numAmount.toString();
            } else {
              const fixedAmount = numAmount.toFixed(2);
              if (parseFloat(fixedAmount) === parseInt(fixedAmount, 10)) {
                  formattedAmount = parseInt(fixedAmount, 10).toString();
              } else {
                  formattedAmount = parseFloat(fixedAmount).toString();
              }
            }
          } else {
            formattedAmount = "Invalid amount";
          }
          otherPrizeStrings.push(`You received ${formattedAmount} ${symbol}.`);
        } catch (e) {
          // Fallback if formatting fails, try to show symbol if available
          otherPrizeStrings.push(`You received an ERC20 token (amount: ${prizeAmountOrId.toString()}, symbol: ${symbol}).`);
        }
      } else if (prizeType === 2) { // AVAX
        try {
          const formattedAmount = ethers.utils.formatEther(prizeAmountOrId);
          const numAmount = Number(formattedAmount);
          let displayAmount = formattedAmount;
          if (numAmount >= 1000000) { 
            displayAmount = formatLargeNumber(numAmount);
          } else if (Number.isInteger(numAmount)) {
            displayAmount = numAmount.toString();
          } else {
            const fixedAmount = numAmount.toFixed(2);
            if (parseFloat(fixedAmount) === parseInt(fixedAmount, 10)) {
                displayAmount = parseInt(fixedAmount, 10).toString();
            } else {
                displayAmount = parseFloat(fixedAmount).toString();
            }
          }
          otherPrizeStrings.push(`You received ${displayAmount} AVAX.`);
        } catch (e) {
          otherPrizeStrings.push(`You received AVAX amount ${prizeAmountOrId.toString()}.`);
        }
      } else { // Fallback for unknown prize types
        otherPrizeStrings.push(`Prize Type ${prizeType} ID ${prizeAmountOrId.toString()} Count ${prizeCountFromEvent ?? 0} Addr ${prizeAddr}.`);
      }
    });

    const uniqueOtherPrizes = Array.from(new Set(otherPrizeStrings)); 

    const assembledPrizeMessages: string[] = [];
    if (nftCount > 0) {
      assembledPrizeMessages.push(`You acquired ${nftCount} NFT${nftCount > 1 ? 's' : ''}.`);
    }

    const actualPrizes = uniqueOtherPrizes.filter(p => p !== "You received nothing." && p !== "Prize details missing for an item.");
    
    if (actualPrizes.length > 0) {
        assembledPrizeMessages.push(...actualPrizes);
    } else if (uniqueOtherPrizes.includes("You received nothing.")) {
        if (nftCount === 0) { 
             assembledPrizeMessages.push("You received nothing.");
        }
    } else if (uniqueOtherPrizes.includes("Prize details missing for an item.") && assembledPrizeMessages.length === 0) {
        assembledPrizeMessages.push("Prize details are unclear for one or more items.");
    }
    
    if (assembledPrizeMessages.length === 0) {
      if (firstEvent?.prizetype === 0 && uniqueOtherPrizes.includes("You received nothing.")) {
           return baseText + " You received nothing.";
      }
      return baseText + " Encounter outcome is unclear.";
    }

    return baseText + " " + assembledPrizeMessages.join(" ");
  };

  return (
    <>
      <style>{`
        /* Basic styles that might have been in shakeAnimation or are global */
        .main::after { display: none; } /* Example: if old scanline came from main */
        .prize-details-after-video { /* Styles for any inline prize details if ever used */
          margin-top: 10px; 
          text-align: center; 
          color: white; 
        }
        /* Style for NFT Name in Prize Display */
        .prize-nft-name {
          font-family: sans-serif; /* Use a standard font */
          font-weight: bold;
          margin-bottom: 2px; /* Reduce space below NFT name */
        }
        /* Style for Collection Name in Prize Display */
        .prize-collection-name {
          font-family: sans-serif; /* Use a standard font */
          font-size: 0.9em;
          color: #aaa; /* Lighter gray color */
          margin-top: 0px; /* Remove extra space above collection name */
          margin-bottom: 5px; /* Add some space below collection name */
        }
        /* Injecting the new styles for the prize result display */
        ${prizeResultDisplayStyles}

        /* Icon hover animation */
        .icon-link {
          transition: transform 0.2s ease-in-out, filter 0.2s ease-in-out;
          display: inline-block; /* Ensures transform applies correctly */
        }
        .icon-link:hover {
          transform: scale(1.2);
          filter: brightness(1.5);
        }
        
        /* Video overlay and container adjustments for mobile */
        .video-overlay .video-container {
          width: 80vw; /* Use 80% of viewport width */
          max-width: 900px; /* Optional: max width for larger screens */
          padding: 0; /* Add some vertical padding */
          /* Ensure it's centered if there's horizontal space */
          margin-left: auto;
          margin-right: auto;
          position: relative; /* Added for positioning the skip button */
        }
        .prize-video {
          max-width: 100%; /* Ensure video scales down to fit container */
          height: auto; /* Maintain aspect ratio */
          border-radius: 10px; /* Optional: rounded corners for the video */
        }

        /* Styling for the main "Enter The City" heading */
        .main .fa.head {
          font-size: 54px; /* Default desktop size */
          margin: 0;
          text-align: center;
          color: white; /* Ensure color is maintained if not specified by .fa or .head */
        }

        @media (max-width: 768px) {
          .main .fa.head {
            font-size: 36px; /* Smaller font size for mobile */
          }
        }

        /* Skip button for video player */
        .video-skip-button {
          position: absolute;
          top: 10px;
          right: 10px;
          background-color: rgba(0, 0, 0, 0.5);
          color: white;
          border: 2px solid white;
          border-radius: 50%;
          width: 30px;
          height: 30px;
          font-size: 20px;
          line-height: 26px; /* Adjust for vertical centering of X */
          text-align: center;
          cursor: pointer;
          z-index: 2100; /* Ensure it's above the video */
        }
        .video-skip-button:hover {
          background-color: rgba(0, 0, 0, 0.8);
        }

      `}</style>
      <div className="scanline-overlay" />
      <LoadingIndicator isActive={isLoading || (isWalletConnected && isInitializing)} /> {/* Show loading when connected but initializing */}
      <Notification message={notification.message} type={notification.type} isVisible={notification.isVisible} onClose={hideNotification} />
      <RewardAnimation amount={rewardAnimation.amount} isVisible={rewardAnimation.isVisible} onComplete={hideRewardAnimation} />
      
      {!isWalletConnected ? (
        <LandingPage />
      ) : (
        <>
          <div className="main bg1" style={{ minHeight: '60vh', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'center', padding: '30px 0', position: 'relative' }}>
            <div style={{ width: '100%', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <h1 className="fa head">
                Enter The City
                {userDisplayName && <span>, {userDisplayName}</span>}
              </h1>
            </div>
            <div style={{ width: '100%', marginTop: '60px' }}>
              <div className="icon-links" style={{ display: 'flex', justifyContent: 'center', gap: '15px' }}>
                <a href="https://x.com/TheAtriumDAO" target="_blank" rel="noopener noreferrer" className="icon-link"><XLogoIcon /></a>
                <a href="https://discord.gg/r7j5QgbfCB" target="_blank" rel="noopener noreferrer" className="icon-link"><DiscordIcon /></a>
                <a href="https://analogdistortions.com" target="_blank" rel="noopener noreferrer" className="icon-link"><ExternalLinkIcon /></a>
                <a href="https://bullet.exchange/collections/66fee59823bad0b9b3584bd2" target="_blank" rel="noopener noreferrer" className="icon-link" title="Buy/Sell Distortions on Bullet Exchange"><ExchangeIcon /></a>
              </div>
              <div className="icon-links" style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '10px' }}> {/* New row for RoleClaim and BlubStaking */}
                <a href="/roleclaim" className="icon-link"><UserIcon /></a>
                <Link to="/blubstaking" className="icon-link"><CoinsIcon /></Link>
                {/* Merchandise link with clothing icon */}
                <a href="https://www.bonfire.com/store/the-city-2/" target="_blank" rel="noopener noreferrer" className="icon-link" title="Merchandise"><ClothingIcon /></a>
              </div>
            </div>
            {/* The ConnectWallet button was here, it's now part of LandingPage or implicitly handled by isWalletConnected */}
          </div>
          
          <div className="section-divider-animated"></div>
          
          <div className="bg-secondary">
            <div className="colbox">
              {isWalletConnected && (ownedTokens.length > 0 || hasTokenStaked) && (
                <div className={`colbox bg2 ${ ownedTokens && ownedTokens.length === 0 && hasTokenStaked ? 'staked-only-view' : ownedTokens && ownedTokens.length > 0 && !hasTokenStaked ? 'unstaked-only-view' : '' }`}>
                  {!(ownedTokens && ownedTokens.length === 0 && hasTokenStaked) && (
                    <><h2 className="fa topmar">Stake Your Analog Distortions</h2>{!(ownedTokens && ownedTokens.length > 0 && !hasTokenStaked) && <div className="section-divider"></div>}{!(ownedTokens && ownedTokens.length > 0 && !hasTokenStaked) && <div className="rowboxGAP widthboost"></div>}</>
                  )}
                  <div className="rowboxGAP">
                    <div className="rmar">
                      <div className="colbox">
                        {ownedTokens && ownedTokens.length > 0 ? (
                          ownedTokens.length <= 2 ? (
                            <div className="large-nft-display">
                              {ownedTokens.map((nft) => <UnstakedNFTCard key={nft.tokenId} tokenId={parseInt(nft.tokenId)} onStake={handleStake} distortionsContractAddress={distortionsContractAddress} stakingContractAddress={stakingContractAddress} isLargeDisplay={true} /> )}
                            </div>
                          ) : (
                            <div className="nft-grid">
                              {ownedTokens.map((nft) => <UnstakedNFTCard key={nft.tokenId} tokenId={parseInt(nft.tokenId)} onStake={handleStake} distortionsContractAddress={distortionsContractAddress} stakingContractAddress={stakingContractAddress} /> )}
                            </div>
                          )
                        ) : (<div className="empty-container"><p className="fb no-unstaked-message">No unstaked NFTs found</p></div>)}
                        
                        {hasTokenStaked && (
                          <div className={ownedTokens && ownedTokens.length === 0 ? 'staked-only-container' : ''}>
                            <h3 className={`fa ${ownedTokens && ownedTokens.length === 0 ? 'no-botmar staked-only-title white-text' : 'botmar'}`}>{ownedTokens && ownedTokens.length === 0 ? 'Staked Analog Distortions' : 'Staked Distortions:'}</h3>
                            {stakedTokens.length <= 2 ? (
                              <div className="large-nft-display">
                                {stakedTokens.map((stakedToken) => <NFTCard tokenId={stakedToken.toNumber()} key={stakedToken.toString()} onUnstake={handleUnstake} isLargeDisplay={true} /> )}
                              </div>
                            ) : (
                              <div className="nft-grid">
                                {stakedTokens.map((stakedToken) => <NFTCard tokenId={stakedToken.toNumber()} key={stakedToken.toString()} onUnstake={handleUnstake} /> )}
                              </div>
                            )}
                          </div>
                        )}
                        <div className="colbox center-column">
                          <div className="balance-item fb claim-pending-display"><b>{!claimableRewards ? "Loading..." : formatClaimableRewards()}</b><b>AURA Pending</b></div>
                          <Web3Button className="ccbutton claim-rewards-button" action={handleClaimRewards} contractAddress={stakingContractAddress}><h3 className="fb">Claim Rewards</h3></Web3Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {isWalletConnected && (
                <div className={`colbox aura-stats-section`} style={{ marginTop: "10px", marginBottom: "10px" }}>
                  <h2 className="fa">Aura Stats:</h2>
                  <div className="balance-row padtop padbot">
                    <div className="balance-item fb"><b>{circulatingSupply}</b><b>Circulating Aura</b></div>
                    <div className="balance-item fb"><b>{burnedTokens}</b><b>AURA Burned</b></div>
                  </div>
                </div>
              )}
              {isWalletConnected && (
                <div className={`colbox bg3 botmar ${isExploreHovered ? 'shake' : ''}`}>
                     <div className="rowbox widthboost">
                        <div onMouseEnter={() => setIsExploreHovered(true)} onMouseLeave={() => setIsExploreHovered(false)}> 
                        <Web3Button
                            className={`explorebutton ${
                            (typeof auraBalance?.value === 'undefined' || typeof theCityTokenFee === 'undefined' || auraBalance.value.lt(theCityTokenFee))
                                ? 'disabled-mode' 
                                : needsApproval ? 'approve-mode' : 'explore-mode'
                            }`}
                            contractAddress={ needsApproval && auraContractAddress ? auraContractAddress : theCityContractAddress }
                            action={async () => {
                              if (typeof auraBalance?.value === 'undefined' || typeof theCityTokenFee === 'undefined' || auraBalance.value.lt(theCityTokenFee)) {
                                  showNotification("Not enough AURA to explore.", "error"); return;
                              }
                              if (needsApproval) {
                                  await handleApproval();
                              } else {
                                  await handleExploreOrClaim();
                              }
                            }}
                            isDisabled={isLoading || (typeof auraBalance?.value === 'undefined' || typeof theCityTokenFee === 'undefined' || auraBalance.value.lt(theCityTokenFee))}
                        >
                            <div className="explore-button-content">
                            <h1 className="fa bot">
                                {(typeof auraBalance?.value === 'undefined' || typeof theCityTokenFee === 'undefined' || auraBalance.value.lt(theCityTokenFee))
                                ? "Not Enough Aura"
                                : needsApproval ? "Approve AURA" : "Explore The City"}
                            </h1>
                            </div>
                        </Web3Button>
                        </div>
                    </div>
                    <h2 className="fb topmar gluttony-section">
                        <Tooltip text={`Pay ${theCityTokenFee ? ethers.utils.formatUnits(theCityTokenFee, 18) : '...'} $AURA and ${theCityAvaxFee ? ethers.utils.formatEther(theCityAvaxFee) : '...'} $AVAX to explore The City and win prizes.`}>
                        Gluttony's Indulgence<br />
                        {theCityTokenFee ? formatLargeNumber(Number(ethers.utils.formatUnits(theCityTokenFee, 18))) : 'Loading...'} AURA<br />
                        {theCityAvaxFee ? ethers.utils.formatEther(theCityAvaxFee) : 'Loading...'} AVAX
                        </Tooltip>
                    </h2>
                    <div className="volume-control-container">
                      <button className="volume-button" onClick={() => setIsVolumeSliderVisible(!isVolumeSliderVisible)} title="Volume Control">
                        {videoVolume === 0 ? <span className="volume-icon">🔇</span> : videoVolume < 0.5 ? <span className="volume-icon">🔉</span> : <span className="volume-icon">🔊</span>}
                      </button>
                      {isVolumeSliderVisible && (
                        <div className="volume-slider-container">
                          <input type="range" min="0" max="1" step="0.01" value={videoVolume} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setVideoVolume(parseFloat(e.target.value))} className="volume-slider" />
                          <span className="volume-percentage">{Math.round(videoVolume * 100)}%</span>
                        </div>
                      )}
                    </div>
                    {errorMessage && ( <div className="error-message fb"> {errorMessage} </div> )}
                    <h4 className="fb holdings-label">YOUR HOLDINGS</h4>
                    <div className="compact-token-display">
                      <div className="compact-token"><span className="fb">AURA</span><span className="mini-token-amount">{auraBalance ? formatLargeNumber(Number(auraBalance.displayValue)) : "0.00"}</span></div>
                      <div className="compact-token"><span className="fb">AVAX</span><span className="mini-token-amount">{avaxBalance ? formatLargeNumber(Number(avaxBalance)) : "0.00"}</span></div>
                      <div className="compact-token"><span className="fb">HERESY</span><span className="mini-token-amount">{heresyBalance ? Math.floor(Number(heresyBalance.displayValue)).toLocaleString() : "0"}</span></div>
                    </div>
                    {heresyBalance && Number(heresyBalance.displayValue) > 0.01 && (
                      <Link to="/tokenswap" className="ccbutton" style={{ marginTop: '15px', textDecoration: 'none' }}>
                        <p className="fb" style={{ margin: '0', padding: '3px 8px', fontSize: '0.9em' }}>Swap Heresy to Aura</p>
                      </Link>
                    )}
                </div>
              )}
            </div>
            <span className="developer-credit fb">Developed by <a href="https://x.com/SnapsNoCaps" target="_blank" rel="noopener noreferrer">snaps</a> with 🩵</span>
          </div>

          {/* Display for Prize Result After Video */}
          {prizeResultToDisplay && (
            <div className="prize-result-display-overlay">
              <div className="prize-result-display-content">
                {/* Use event name from the first event for the title */}
                <h3>{prizeResultToDisplay && prizeResultToDisplay[0]?.eventID !== undefined && FRONTEND_EVENTS_MAP[prizeResultToDisplay[0].eventID]?.name || 'Exploration Result'}</h3>
                
                {/* Container for NFT displays */}
                <div className="prize-nft-container"> 
                  {prizeResultToDisplay && prizeResultToDisplay
                    .filter(details => details.prizetype === 3 || details.prizetype === 4) // Filter for NFTs
                    .map((nftDetails, index) => (
                      nftDetails.prizeAddress && nftDetails.prizeID && (
                        <NftDisplay 
                          key={`${nftDetails.prizeAddress}-${nftDetails.prizeID.toString()}-${index}`} // Unique key
                          contractAddress={nftDetails.prizeAddress}
                          tokenId={nftDetails.prizeID}
                        />
                      )
                    ))
                  }
                </div>

                {/* Display the formatted text description */}
                <p className="fb prize-description-text">{formatPrizeForDisplay(prizeResultToDisplay)}</p>

                <button
                  className="ccbutton"
                  onClick={() => {
                    setPrizeResultToDisplay(null);
                    setCurrentPrizeDetails(null); // Clear details after dismissing
                  }}
                >
                  <span className="fb">Dismiss</span>
                </button>
              </div>
            </div>
          )}

          {isPlayingVideo && currentVideo && (
            <div className="video-overlay">
              <div className="video-container">
                <button
                  className="video-skip-button"
                  onClick={async () => {
                    setIsPlayingVideo(false);
                    setCurrentVideo(null);
                    if (currentPrizeDetails) {
                      setPrizeResultToDisplay(currentPrizeDetails);
                    }
                    // No need to clear currentPrizeDetails here, it's handled by the prize display dismissal
                    await refreshAllDataCallback();
                  }}
                  title="Skip Video"
                >
                  &times;
                </button>
                <h3 className="fa prize-name">{currentPrizeName || "Encountering..."}</h3>
                <video ref={videoRef} autoPlay
                  onLoadStart={() => console.log("Video loading started:", currentVideo)}
                  onCanPlay={() => console.log("Video can play:", currentVideo)}
                  onPlay={() => console.log("Video started playing")}
                  onEnded={async () => {
                    setIsPlayingVideo(false); 
                    setCurrentVideo(null); 
                    // Don't clear currentPrizeName immediately if it's used by the result display title
                    if (currentPrizeDetails) {
                        setPrizeResultToDisplay(currentPrizeDetails); // Set details for the modal/display
                    }
                    // currentPrizeDetails itself will be cleared when the new display is dismissed
                    // setCurrentPrizeDetails(null); // Don't clear here, clear on dismiss of new UI
                    await refreshAllDataCallback(); 
                  }}
                  onError={(e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
                    console.error("Video playback error:", e, currentVideo);
                    setIsPlayingVideo(false); setCurrentVideo(null); setCurrentPrizeName(null);
                    // If video errors, still show any prize details we might have captured
                    // Trigger display even on video error
                    if (currentPrizeDetails) {
                        setPrizeResultToDisplay(currentPrizeDetails);
                    }
                    setIsLoading(false); // Ensure loading stops on error
                    // Ensure isInitializing is also false if this somehow happens during init phase.
                    // Though typically this would be after initial load.
                    if (isInitializing) setIsInitializing(false); 
                    showNotification("Error playing prize video. Prize details (if any) shown.", "error");
                    refreshAllDataCallback(); // Use the callback version
                  }}
                  className="prize-video"
                >
                  <source src={currentVideo} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                {/* Removed prize details from here as they will be shown after */}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default App;

